import { useState, useEffect } from 'react';
import { View } from 'react-native';
import { useTheme } from '@/theme';
import { ThemedText, Caption } from '@/components/ui/Text';
import { formatCurrency, convertCurrency, type CurrencyCode } from '@/utils/currency';

type DualCurrencyDisplayProps = {
  amount: number;
  currency: CurrencyCode;
  baseCurrency: CurrencyCode;
  showRate?: boolean;
  size?: 'small' | 'medium' | 'large';
  /** When false, skip live conversion calls and render only original amount unless snapshot exists. */
  allowLiveConversion?: boolean;
  /** When set (e.g. from Firestore), avoids a network call and matches the saved trip conversion. */
  savedConversion?: {
    convertedAmount: number;
    exchangeRate: number;
    toCurrency: string;
  };
};

/**
 * Displays amount in original currency with converted amount in base currency
 * Only shows conversion if currencies differ
 */
export function DualCurrencyDisplay({
  amount,
  currency,
  baseCurrency,
  showRate = false,
  size = 'medium',
  allowLiveConversion = true,
  savedConversion,
}: DualCurrencyDisplayProps) {
  const theme = useTheme();
  const [convertedAmount, setConvertedAmount] = useState<number | null>(null);
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const isSameCurrency = currency.toUpperCase() === baseCurrency.toUpperCase();
  const useSnapshot =
    !isSameCurrency &&
    savedConversion &&
    savedConversion.toCurrency.toUpperCase() === baseCurrency.toUpperCase() &&
    typeof savedConversion.convertedAmount === 'number' &&
    Number.isFinite(savedConversion.convertedAmount);

  useEffect(() => {
    if (isSameCurrency) {
      setConvertedAmount(null);
      setExchangeRate(null);
      return;
    }

    if (useSnapshot && savedConversion) {
      setConvertedAmount(savedConversion.convertedAmount);
      setExchangeRate(savedConversion.exchangeRate);
      setLoading(false);
      return;
    }

    if (!allowLiveConversion) {
      setLoading(false);
      setConvertedAmount(null);
      setExchangeRate(null);
      return;
    }

    setLoading(true);
    convertCurrency(amount, currency, baseCurrency)
      .then((converted) => {
        setConvertedAmount(converted);
        // Calculate rate
        const rate = amount !== 0 ? converted / amount : 0;
        setExchangeRate(rate);
      })
      .catch((error) => {
        console.error('[DualCurrencyDisplay] Conversion error:', error);
        setConvertedAmount(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    amount,
    currency,
    baseCurrency,
    isSameCurrency,
    allowLiveConversion,
    useSnapshot,
    savedConversion?.convertedAmount,
    savedConversion?.exchangeRate,
    savedConversion?.toCurrency,
  ]);

  const fontSize = size === 'small' ? 13 : size === 'large' ? 18 : 15;
  const convertedFontSize = size === 'small' ? 10 : size === 'large' ? 13 : 11;

  if (isSameCurrency) {
    return (
      <ThemedText variant="bodyStrong" style={{ fontSize }}>
        {formatCurrency(amount, currency)}
      </ThemedText>
    );
  }

  return (
    <View style={{ alignItems: 'flex-end', gap: theme.spacing(0.25) }}>
      <ThemedText variant="bodyStrong" style={{ fontSize }}>
        {formatCurrency(amount, currency)}
      </ThemedText>
      {loading ? (
        <Caption color={theme.colors.textSecondary} style={{ fontSize: convertedFontSize }}>
          Converting...
        </Caption>
      ) : convertedAmount !== null ? (
        <>
          <Caption color={theme.colors.textSecondary} style={{ fontSize: convertedFontSize }}>
            ≈ {formatCurrency(convertedAmount, baseCurrency)}
          </Caption>
          {showRate && exchangeRate !== null && (
            <Caption color={theme.colors.textSecondary} style={{ fontSize: convertedFontSize - 1 }}>
              1 {currency.toUpperCase()} = {exchangeRate.toFixed(4)} {baseCurrency.toUpperCase()}
              {useSnapshot ? ' (when logged)' : ''}
            </Caption>
          )}
        </>
      ) : null}
    </View>
  );
}

